import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  DollarSign, Plus, Trash2, Edit2, Receipt, Car, 
  X, Camera, TrendingUp, Lock, Check
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobExpenses({ jobId, quoteAmount = 0, onExpenseChange }) {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [categories, setCategories] = useState([]);
  const [mileageRate, setMileageRate] = useState(0.67);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const fileInputRef = useRef(null);
  
  const [form, setForm] = useState({
    amount: '',
    description: '',
    category: 'materials',
    receipt_photo: '',
    mileage: '',
    is_billable: false,
    expense_date: format(new Date(), 'yyyy-MM-dd')
  });

  useEffect(() => {
    fetchCategories();
    fetchLimits();
    if (jobId) {
      fetchExpenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`);
      setCategories(response.data.categories || []);
      setMileageRate(response.data.mileage_rate || 0.67);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchLimits = async () => {
    try {
      const response = await axios.get(`${API}/expenses/limits`);
      setLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch limits');
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/expenses`);
      setExpenses(response.data.expenses || []);
      setSummary(response.data.summary || null);
      setProfitability(response.data.profitability || null);
      
      // Notify parent of billable expenses change
      if (onExpenseChange) {
        onExpenseChange(response.data.summary?.billable_expenses || 0);
      }
    } catch (error) {
      console.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.amount || !form.description || !form.category) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        mileage: form.mileage ? parseFloat(form.mileage) : null,
        job_id: jobId
      };

      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/expenses`, payload);
        toast.success('Expense added');
      }

      setShowModal(false);
      resetForm();
      fetchExpenses();
      fetchLimits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/expenses/${id}`);
      toast.success('Expense deleted');
      fetchExpenses();
      fetchLimits();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setForm({
      amount: expense.amount.toString(),
      description: expense.description,
      category: expense.category,
      receipt_photo: expense.receipt_photo || '',
      mileage: expense.mileage?.toString() || '',
      is_billable: expense.is_billable,
      expense_date: expense.expense_date
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingExpense(null);
    setForm({
      amount: '',
      description: '',
      category: 'materials',
      receipt_photo: '',
      mileage: '',
      is_billable: false,
      expense_date: format(new Date(), 'yyyy-MM-dd')
    });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!limits?.has_receipt_photos) {
      toast.error('Receipt photos require Solo Pro plan');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({ ...prev, receipt_photo: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const isPaidPlan = user?.subscription_tier !== 'free';

  return (
    <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold text-slate-dark flex items-center gap-2">
          <Receipt className="w-5 h-5 text-[#FF5F00]" />
          Job Expenses
        </h2>
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          size="sm"
          className="bg-[#FF5F00]"
          data-testid="add-job-expense-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      {/* Profitability Summary */}
      {profitability && (
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-4 mb-4 border border-emerald-200">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <span className="font-bold text-emerald-800">Job Profitability</span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-xs text-slate-500 uppercase">Quoted</p>
              <p className="font-heading text-lg font-bold text-slate-900">
                ${profitability.quoted.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Expenses</p>
              <p className="font-heading text-lg font-bold text-red-600">
                -${profitability.expenses.toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-2">
              <p className="text-xs text-emerald-600 uppercase font-bold">Take Home</p>
              <p className="font-heading text-xl font-bold text-emerald-700">
                ${profitability.take_home.toFixed(2)}
              </p>
              <p className="text-xs text-emerald-600">({profitability.profit_margin}%)</p>
            </div>
          </div>
        </div>
      )}

      {/* Expense List */}
      {loading ? (
        <div className="text-center py-4 text-slate-400">Loading...</div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-6 text-slate-400">
          <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No expenses for this job yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {expenses.map(expense => (
            <div 
              key={expense.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                expense.is_billable ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'
              }`}
              data-testid={`job-expense-${expense.id}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900 text-sm">{expense.description}</p>
                  {expense.is_billable && (
                    <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">
                      BILLABLE
                    </span>
                  )}
                  {expense.receipt_photo && (
                    <Camera className="w-3 h-3 text-emerald-600" />
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {expense.category_name} • {format(new Date(expense.expense_date), 'MMM d')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="font-bold text-red-600">${expense.amount.toFixed(2)}</p>
                  {expense.mileage && (
                    <p className="text-[10px] text-blue-600">{expense.mileage} mi</p>
                  )}
                </div>
                <button 
                  onClick={() => handleEdit(expense)}
                  className="p-1 hover:bg-white rounded"
                >
                  <Edit2 className="w-3 h-3 text-slate-400" />
                </button>
                <button 
                  onClick={() => handleDelete(expense.id)}
                  className="p-1 hover:bg-red-100 rounded"
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              </div>
            </div>
          ))}
          
          {/* Total */}
          {summary && (
            <div className="flex justify-between pt-2 border-t border-slate-200 mt-2">
              <span className="text-sm text-slate-600">Total Expenses</span>
              <span className="font-bold text-red-600">${summary.total_expenses.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold">
                {editingExpense ? 'Edit Expense' : 'Add Job Expense'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Amount */}
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="pl-9 h-12"
                    placeholder="0.00"
                    data-testid="job-expense-amount"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Description *</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="h-12"
                  placeholder="e.g., Fertilizer from Home Depot"
                  data-testid="job-expense-description"
                />
              </div>

              {/* Category */}
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Category *</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full h-12 px-3 border-2 border-slate-200 rounded-lg bg-white"
                  data-testid="job-expense-category"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Date</Label>
                <Input
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => setForm(prev => ({ ...prev, expense_date: e.target.value }))}
                  className="h-12"
                />
              </div>

              {/* Billable Toggle */}
              <div className={`flex items-center justify-between p-3 rounded-lg border-2 ${
                form.is_billable ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'
              } ${!limits?.has_billable_expenses ? 'opacity-50' : ''}`}>
                <div>
                  <p className="font-bold text-slate-900 flex items-center gap-2">
                    Bill to Client
                    {!limits?.has_billable_expenses && <Lock className="w-3 h-3" />}
                  </p>
                  <p className="text-xs text-slate-500">Add this expense to the client's invoice</p>
                </div>
                <button
                  onClick={() => {
                    if (!limits?.has_billable_expenses) {
                      toast.error('Billable expenses require Solo Pro plan');
                      return;
                    }
                    setForm(prev => ({ ...prev, is_billable: !prev.is_billable }));
                  }}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    form.is_billable ? 'bg-amber-500' : 'bg-slate-300'
                  }`}
                  disabled={!limits?.has_billable_expenses}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    form.is_billable ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Mileage */}
              <div className={!limits?.has_mileage ? 'opacity-50' : ''}>
                <Label className="text-xs font-bold uppercase text-slate-500 flex items-center gap-2">
                  <Car className="w-4 h-4" /> Mileage
                  {!limits?.has_mileage && <Lock className="w-3 h-3" />}
                </Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    step="0.1"
                    value={form.mileage}
                    onChange={(e) => setForm(prev => ({ ...prev, mileage: e.target.value }))}
                    className="h-12 flex-1"
                    placeholder="Miles driven for this job"
                    disabled={!limits?.has_mileage}
                  />
                  {form.mileage && (
                    <span className="text-sm text-blue-600 font-bold whitespace-nowrap">
                      = ${(parseFloat(form.mileage || 0) * mileageRate).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>

              {/* Receipt Photo */}
              <div className={!limits?.has_receipt_photos ? 'opacity-50' : ''}>
                <Label className="text-xs font-bold uppercase text-slate-500 flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Receipt Photo
                  {!limits?.has_receipt_photos && <Lock className="w-3 h-3" />}
                </Label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  ref={fileInputRef}
                  disabled={!limits?.has_receipt_photos}
                />
                {form.receipt_photo ? (
                  <div className="relative">
                    <img 
                      src={form.receipt_photo} 
                      alt="Receipt" 
                      className="w-full h-32 object-cover rounded-lg border-2 border-slate-200"
                    />
                    <button
                      onClick={() => setForm(prev => ({ ...prev, receipt_photo: '' }))}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-12"
                    disabled={!limits?.has_receipt_photos}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {limits?.has_receipt_photos ? 'Snap Receipt' : 'Upgrade for Photos'}
                  </Button>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 h-12">
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="flex-1 h-12 bg-[#FF5F00]" data-testid="save-job-expense-btn">
                {editingExpense ? 'Update' : 'Add Expense'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
